import React from 'react';
import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';
import { PrimaryButton } from 'components/theme/Button';
import { ANALYTICS_EVENT } from '@kouto/types';
import { SkeletonLine } from 'components/theme/Skeleton';
import { Error, ErrorContainer } from 'components/theme/Form';
import CheckBox from 'components/theme/CheckBox/CheckBox';
import { BOOKING_MODES } from 'types/booking';
import { TIER_PARTICIPANTS } from 'types/participants';
import { BOOKING_AVAILABILITY_MODE_NON_RESTRICTED } from 'types/experience';
import { TicketSalesState } from 'hooks/useLimitedTimeStatus';
import { isSessionMultipleTiers } from 'utils';
import PeopleIcon from 'assets/icon-user';
import OpenMenuIcon from 'assets/chevron-down';
import SessionPrice from './sub-components/SessionPrice';
import PartySize from './sub-components/PartySize';
import ParticipantsDropdown from './sub-components/ParticipantsDropdown';
import DateView from './sub-components/DateView';
import SingleSessionDatePicker from './SingleSessionDatePicker';
import ParticipantsSelector from './ParticipantsSelector';
import DurationSelector from './DurationSelector';
import SessionTimeSelector from './SessionTimeSelector';
import AccessCodeInput from './AccessCodeInput';
import AdditionalReminderSection from './AdditionalReminderSection';
import DateRangeSelector from '../molecules/DateRangeSelector/DateRangeSelector';
import ErrorMessage from './ErrorMessage';
import SelectWrapper from './SelectWrapper';
import * as SC from './styles';
import { dropdownBtnStyles } from './styles';

/* eslint react/prop-types: 'off' */
const ExperienceBookingSidebar = ({
  experience,
  listing,
  isLoadingListing,
  isLoadingCustomQuestions,
  isRecurring,
  isOneTime,
  hideWidgets,
  isRequestToBookEnabled,
  isMobile,
  searchParams,
  control,
  errors,
  handleSubmit,
  clearErrors,
  selectedEventSession,
  isPrivateMode,
  availablePriceTiers,
  startDateCalendar,
  endDateCalendar,
  selectedSessionDateAvailabilityCalendar,
  setSelectedSessionDateAvailabilityCalendar,
  isDropdownVisible,
  setIsDropdownVisible,
  availableSeatsForSession,
  totalSelectedParticipants,
  isFetchingSessionTiers,
  setSinglePriceTierCount,
  cartExperience,
  participantValidationMessage,
  setParticipantValidationMessage,
  dateRangeValidationMessage,
  durations,
  selectedDuration,
  setSelectedDuration,
  experienceStartTimes,
  isUnavailable,
  selectedStartTime,
  setSelectedStartTime,
  accessCodeNumber,
  setAccessCodeNumber,
  availableDates,
  codeIsRequiredMsg,
  containsRemindersText,
  isNoSeatsAvailableForSession,
  isSoldOut,
  isWithinCuttoff,
  validateAccessCodeStatus,
  bookButtonLabel,
  limitedTime,
  calendarLoading,
  setCalendarLoading,
  isDayHighlighted,
  handleOnDateChange,
  onPrevMonthClick,
  onNextMonthClick,
  onSessionDatePrevMonthClick,
  onSessionDateNextMonthClick,
  onPrevDateRange,
  onNextDateRange,
  onDropdownToggle,
  onTierDropdownClick,
  handlePrivateCheckboxChange,
  handleBooking,
  getBookButtonAnalyticData,
  type,
  isFlexibleDurationEnabled,
  fetchFirstAvailabilityStatus,
}) => {
  const { t: translate } = useTranslation();
  const displayCurrency = listing?.currency ?? experience?.currency;
  const displayTaxes = listing?.taxes ?? experience?.taxes;

  return (
    <div
      style={{
        width: isMobile ? '100%' : 'auto',
      }}
    >
      <SC.ExperiencesBodySidebar
        as={isMobile ? 'div' : 'aside'}
        aria-label={translate('bookExperiencePanel')}
        className="experience-view__experience-body-sidebar"
        loading={experience.loading}
        onClick={onDropdownToggle}
      >
        {isLoadingListing ? (
          <SkeletonLine translucent style={{ height: '23px' }} />
        ) : (
          <SessionPrice
            className="experience-view-sidebar-price"
            experience={experience}
            currency={displayCurrency}
            selectedSession={selectedEventSession}
            isPrivateBooking={isPrivateMode}
            taxes={displayTaxes}
          />
        )}
        {!experience.loading || !isLoadingListing ? (
          (isRecurring && (
            <SC.SidebarTitle className="experience-view-sidebar-title">
              {listing?.title}
            </SC.SidebarTitle>
          )) ||
          null
        ) : (
          <SkeletonLine translucent />
        )}
        {!experience.loading ? (
          (isRecurring && !hideWidgets && <PartySize />) || null
        ) : (
          <SkeletonLine translucent />
        )}
        {!experience.loading ? (
          (isRecurring && (
            <SC.SidebarForm
              className="experience-view__sidebar-form"
              onSubmit={handleSubmit(handleBooking)}
            >
              {experience.bookingAvailabilityMode ===
                BOOKING_AVAILABILITY_MODE_NON_RESTRICTED && (
                <SC.PrivateCheckboxContainer>
                  <Controller
                    control={control}
                    onChange={([selected]) => {
                      handlePrivateCheckboxChange(selected);
                      return selected;
                    }}
                    name="isPrivateSession"
                    valueName="checked"
                    defaultValue={
                      searchParams?.mode
                        ? searchParams?.mode === BOOKING_MODES.PRIVATE
                        : isPrivateMode
                    }
                    render={({ field }) => {
                      return (
                        <CheckBox
                          id="isPrivateMode"
                          label={translate('viewPrivateSessAvailability')}
                          {...field}
                        />
                      );
                    }}
                  />
                </SC.PrivateCheckboxContainer>
              )}
              {isRequestToBookEnabled && !hideWidgets && (
                <SC.RequestToBookSideBarWrapper>
                  <DateRangeSelector
                    endDate={endDateCalendar}
                    isDayHighlighted={isDayHighlighted}
                    onDateChange={handleOnDateChange}
                    position="right"
                    startDate={startDateCalendar}
                    onPrevMonthClick={onPrevMonthClick}
                    onNextMonthClick={onNextMonthClick}
                    numberOfMonthsToDisplay={isMobile ? 1 : 2}
                  />
                  {dateRangeValidationMessage && (
                    <ErrorContainer>
                      <Error>{dateRangeValidationMessage}</Error>
                    </ErrorContainer>
                  )}
                  <PrimaryButton
                    big="true"
                    className="experience-view__book-button"
                    type="submit"
                    disabled={
                      !startDateCalendar || !endDateCalendar || isUnavailable
                    }
                    analyticEvent={
                      ANALYTICS_EVENT.RESERVABLE_EXPERIENCE_CLICK_BOOK_BUTTON
                    }
                    analyticData={getBookButtonAnalyticData()}
                  >
                    {translate('request')}
                  </PrimaryButton>
                </SC.RequestToBookSideBarWrapper>
              )}
              {!isRequestToBookEnabled && !hideWidgets && (
                <SC.AvailabilitySessions>
                  <SingleSessionDatePicker
                    errors={errors}
                    isDayHighlighted={isDayHighlighted}
                    isMobile={isMobile}
                    setCalendarLoading={setCalendarLoading}
                    onSessionDatePrevMonthClick={onSessionDatePrevMonthClick}
                    onSessionDateNextMonthClick={onSessionDateNextMonthClick}
                    selectedSessionDateAvailabilityCalendar={
                      selectedSessionDateAvailabilityCalendar
                    }
                    setSelectedSessionDateAvailabilityCalendar={
                      setSelectedSessionDateAvailabilityCalendar
                    }
                    isDropdownVisible={isDropdownVisible}
                    isCalendarLoading={calendarLoading}
                    clearErrors={clearErrors}
                  />
                  <ParticipantsSelector
                    availablePriceTiers={availablePriceTiers}
                    availableSeatsForSession={availableSeatsForSession}
                    control={control}
                    errors={errors}
                    isDropdownVisible={isDropdownVisible}
                    isFetchingSessionTiers={isFetchingSessionTiers}
                    setIsDropdownVisible={setIsDropdownVisible}
                    setSinglePriceTierCount={setSinglePriceTierCount}
                    totalSelectedParticipants={totalSelectedParticipants}
                    clearErrors={clearErrors}
                  />
                </SC.AvailabilitySessions>
              )}
              {isDropdownVisible && !hideWidgets && (
                <SC.TierDropdown
                  className="experience-view__tier-dropdown"
                  data-dropdownid={TIER_PARTICIPANTS}
                  onClick={onTierDropdownClick}
                >
                  {availablePriceTiers?.map((priceCategory, index) => {
                    return (
                      <ParticipantsDropdown
                        key={`${priceCategory + index}`}
                        cartExperienceSession={cartExperience}
                        priceCategory={priceCategory}
                        index={index}
                        setParticipantValidationMessage={
                          setParticipantValidationMessage
                        }
                        clearErrors={clearErrors}
                        currency={displayCurrency}
                        taxes={displayTaxes}
                      />
                    );
                  })}
                  <PrimaryButton
                    big="true"
                    className="experience-view__book-button"
                    style={dropdownBtnStyles}
                    onClick={(e) => {
                      setIsDropdownVisible(false);
                      e.stopPropagation();
                    }}
                  >
                    <span>{translate('apply')}</span>
                  </PrimaryButton>
                </SC.TierDropdown>
              )}
              {!isRequestToBookEnabled && !hideWidgets && (
                <>
                  <DurationSelector
                    isFlexibleDurationEnabled={isFlexibleDurationEnabled}
                    durations={durations}
                    selectedDuration={selectedDuration}
                    setSelectedDuration={setSelectedDuration}
                  />
                  <SessionTimeSelector
                    control={control}
                    durations={durations}
                    experienceStartTimes={experienceStartTimes}
                    fetchFirstAvailabilityStatus={fetchFirstAvailabilityStatus}
                    isFetchingSessionTiers={isFetchingSessionTiers}
                    isUnavailable={isUnavailable}
                    setSelectedStartTime={setSelectedStartTime}
                    clearErrors={clearErrors}
                  />
                  <AccessCodeInput
                    accessCodeNumber={accessCodeNumber}
                    availableDates={availableDates}
                    codeIsRequiredMsg={codeIsRequiredMsg}
                    containsRemindersText={containsRemindersText}
                    errors={errors}
                    experience={experience}
                    isNoSeatsAvailableForSession={isNoSeatsAvailableForSession}
                    isSoldOut={isSoldOut}
                    isWithinCuttoff={isWithinCuttoff}
                    selectedStartTime={selectedStartTime}
                    setAccessCodeNumber={setAccessCodeNumber}
                    validateAccessCodeStatus={validateAccessCodeStatus}
                  />
                  <SC.BookingSpace containshealthtext={containsRemindersText}>
                    {!experience.isExclusive && !hideWidgets && (
                      <PrimaryButton
                        big="true"
                        className="experience-view__book-button"
                        type="submit"
                        disabled={
                          !experienceStartTimes?.length ||
                          isUnavailable ||
                          isNoSeatsAvailableForSession ||
                          !selectedStartTime ||
                          limitedTime?.state ===
                            TicketSalesState.NOT_YET_OPEN ||
                          isLoadingCustomQuestions
                        }
                        analyticEvent={
                          ANALYTICS_EVENT.RESERVABLE_EXPERIENCE_CLICK_BOOK_BUTTON
                        }
                      >
                        {bookButtonLabel}
                      </PrimaryButton>
                    )}
                  </SC.BookingSpace>
                  <SC.ValidationMessageBlock>
                    <ErrorMessage
                      errors={errors}
                      name="sessionTime"
                      message={
                        <>
                          <Error style={{ fontSize: '14px' }}>
                            {translate('selectSessionValidation')}
                          </Error>
                          {isSessionMultipleTiers(availablePriceTiers) &&
                            totalSelectedParticipants < 1 && (
                              <ErrorContainer>
                                <ErrorMessage
                                  name="selectParticipantValidation"
                                  message={translate(
                                    'selectParticipantValidation',
                                  )}
                                  style={{ fontSize: '14px' }}
                                />
                              </ErrorContainer>
                            )}
                        </>
                      }
                    />
                    {participantValidationMessage && (
                      <ErrorContainer>
                        <ErrorMessage
                          errors={errors}
                          name="selectParticipantValidation"
                          style={{ fontSize: '14px' }}
                          message={participantValidationMessage}
                        />
                      </ErrorContainer>
                    )}
                  </SC.ValidationMessageBlock>
                </>
              )}
            </SC.SidebarForm>
          )) ||
          null
        ) : (
          <SkeletonLine style={{ height: 300 }} translucent />
        )}

        {!experience.isLoading ? (
          (isOneTime && (
            <SC.SidebarForm
              className="experience-view__sidebar-form"
              onSubmit={handleSubmit(handleBooking)}
            >
              <DateView
                type={type}
                startDateCalendar={startDateCalendar}
                experience={experience}
                onPrevDateRange={onPrevDateRange}
                onNextDateRange={onNextDateRange}
                availableDates={availableDates}
              />
              {!experience.loading ? (
                <PartySize />
              ) : (
                <SkeletonLine translucent />
              )}

              {isDropdownVisible && (
                <SC.OpenTierDropdown
                  data-dropdownid={TIER_PARTICIPANTS}
                  onClick={onTierDropdownClick}
                  isPartySizeEnforced={!!experience?.partySize}
                >
                  {availablePriceTiers?.map((priceCategory, index) => {
                    return (
                      <ParticipantsDropdown
                        key={`${priceCategory + index}`}
                        cartExperienceSession={cartExperience}
                        priceCategory={priceCategory}
                        index={index}
                        setParticipantValidationMessage={
                          setParticipantValidationMessage
                        }
                        clearErrors={clearErrors}
                        currency={displayCurrency}
                        taxes={displayTaxes}
                      />
                    );
                  })}
                  <PrimaryButton
                    big="true"
                    className="experience-view__book-button"
                    style={dropdownBtnStyles}
                    onClick={(e) => {
                      setIsDropdownVisible(false);
                      e.stopPropagation();
                    }}
                  >
                    <span>{translate('apply')}</span>
                  </PrimaryButton>
                </SC.OpenTierDropdown>
              )}

              {!isFetchingSessionTiers &&
                (isSessionMultipleTiers(availablePriceTiers) ? (
                  <>
                    <SC.ParticipantsEventWrapper
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.stopPropagation();
                          setIsDropdownVisible(true);
                        }
                      }}
                      onClick={(e) => {
                        setIsDropdownVisible(true);
                        e.stopPropagation();
                      }}
                    >
                      <SC.WrapperFirst>
                        <PeopleIcon />
                        <span>
                          {totalSelectedParticipants || translate('select')}
                        </span>
                      </SC.WrapperFirst>
                      <SC.WrapperSecond isDropdownVisible={isDropdownVisible}>
                        <div />
                        <OpenMenuIcon />
                      </SC.WrapperSecond>
                    </SC.ParticipantsEventWrapper>
                  </>
                ) : (
                  <SC.ParticipantsBoxContainer>
                    <Controller
                      control={control}
                      rules={{ required: true }}
                      name="noOfGuests"
                      defaultValue={
                        searchParams?.noOfGuests
                          ? {
                              label: searchParams.noOfGuests,
                              value: searchParams.noOfGuests,
                            }
                          : { label: 1, value: 1 }
                      }
                      render={({ field: { onChange, value } }) => (
                        <SelectWrapper
                          options={availableSeatsForSession}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onChange={onChange}
                          value={value}
                        />
                      )}
                    />
                    <ErrorMessage
                      errors={errors}
                      name="noOfGuests"
                      message="No. of Guests is Required"
                    />
                  </SC.ParticipantsBoxContainer>
                ))}

              {isWithinCuttoff ? (
                <SC.CutOffMessage>
                  <SC.DurationIcon />
                  Within online booking cutoff
                </SC.CutOffMessage>
              ) : null}
              {isSoldOut ? (
                <SC.CutOffMessage>{translate('soldOut')}</SC.CutOffMessage>
              ) : null}
              <AccessCodeInput
                accessCodeNumber={accessCodeNumber}
                availableDates={availableDates}
                codeIsRequiredMsg={codeIsRequiredMsg}
                containsRemindersText={containsRemindersText}
                errors={errors}
                experience={experience}
                isNoSeatsAvailableForSession={isNoSeatsAvailableForSession}
                isSoldOut={isSoldOut}
                isWithinCuttoff={isWithinCuttoff}
                selectedStartTime={selectedStartTime}
                setAccessCodeNumber={setAccessCodeNumber}
                validateAccessCodeStatus={validateAccessCodeStatus}
              />
              {!experience.isExclusive && (
                <PrimaryButton
                  big="true"
                  className="experience-view__book-button"
                  type="submit"
                  disabled={
                    availableDates.length < 1 ||
                    isSoldOut ||
                    isWithinCuttoff ||
                    experience?.loading ||
                    availableSeatsForSession?.length === 0 ||
                    isLoadingCustomQuestions
                  }
                  analyticEvent={
                    ANALYTICS_EVENT.RESERVABLE_EXPERIENCE_CLICK_BOOK_BUTTON
                  }
                >
                  {experience?.customCallToAction || translate('book')}
                </PrimaryButton>
              )}
              {participantValidationMessage && (
                <ErrorContainer>
                  <Error>{participantValidationMessage}</Error>
                </ErrorContainer>
              )}
            </SC.SidebarForm>
          )) ||
          null
        ) : (
          <SkeletonLine style={{ height: 300 }} translucent />
        )}
        <AdditionalReminderSection
          containsRemindersText={containsRemindersText}
          experience={experience}
        />
      </SC.ExperiencesBodySidebar>
    </div>
  );
};

export default ExperienceBookingSidebar;
